import {
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { IconClearAll, IconSearch } from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import { quickSearch } from "api/product.service"; // your new endpoint
import { HEADER_HEIGHT } from "components/layouts/consts";
import React, { useEffect, useState } from "react";
import { ProductSearchSummaries } from "types/model.types";
import ProductCard from "./ProductCard";
import { useDebouncedSearch } from "./useDebouncedSearch";

const ProductSearch: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [filteredResults, setFilteredResults] =
    useState<ProductSearchSummaries>([]);
  const debounced = useDebouncedSearch(searchTerm, 300);

  useEffect(() => {
    if (loading) return;

    if (debounced.length < 2) {
      setFilteredResults([]);
      return;
    }
    setLoading(true);

    const fetchData = async () => {
      try {
        const response = await safeApiCall(quickSearch(debounced));
        if (response) {
          setFilteredResults(response ?? []);
        }
      } catch (error) {
        logError(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [debounced]);

  return (
    <>
      <TextInput
        placeholder="Search Products..."
        leftSection={<IconSearch size={16} />}
        onFocus={() => setSearchOpen(true)}
        rightSection={
          <IconClearAll
            onClick={() => {
              setSearchTerm("");
              setSearchOpen(false);
            }}
            size={16}
          />
        }
        style={{ width: 250 }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setSearchTerm("");
            setSearchOpen(false);
          }
        }}
        onChange={(e) => {
          setSearchTerm(e.target.value);
        }}
        value={searchTerm}
      />
      <Modal
        opened={searchOpen}
        onClose={() => {
          setSearchTerm("");
          setSearchOpen(false);
        }}
        withCloseButton={false}
        closeOnEscape
        closeOnClickOutside
        trapFocus={false} // <-- prevents focus stealing
        centered={false}
        withinPortal // <-- ensures correct z-index without focus trap
        overlayProps={{ backgroundOpacity: 0, blur: 0 }}
        padding={0}
        radius="md"
        styles={{
          content: {
            position: "absolute",
            top: HEADER_HEIGHT + 8,
            left: "50%",
            transform: "translateX(-50%)",
            width: "75vw",
            border: "none",
            background: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <Paper
          radius="md"
          shadow="md"
          withBorder
          style={{
            overflow: "hidden",
            maxHeight: "60vh",
            background: "white",
          }}
        >
          <ScrollArea h="60vh" px="sm" py="xs">
            {filteredResults.length === 0 ? (
              <Text size="sm" c="dimmed">
                No matches found
              </Text>
            ) : (
              <Stack gap="sm">
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Product ID</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th>Sell Price 1</Table.Th>
                      <Table.Th>Sell Price 2</Table.Th>
                      <Table.Th>Barcode</Table.Th>
                      <Table.Th>Short Dated</Table.Th>
                      <Table.Th>On Request</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredResults.map((i) => (
                      <ProductCard
                        key={i.id}
                        p={i}
                        onClose={() => {
                          setSearchOpen(false);
                        }}
                      />
                    ))}
                  </Table.Tbody>
                </Table>
              </Stack>
            )}
          </ScrollArea>
        </Paper>
      </Modal>
    </>
  );
};

export default ProductSearch;
